import { useMemo } from 'react'

import { TxnData } from '~/components/txn-display/state-change/types'
import { OrgRegularTransaction, PublicRegularTransaction } from '~/types'
import { BackendTransactionDecodedCall } from '~/types'
import { Address } from '~/utils/address'

export function useBuildPendingStateChangeProps({
  transaction,
  txnData,
}: {
  transaction?: OrgRegularTransaction | PublicRegularTransaction
  txnData?: TxnData
}) {
  const smartAccountAddress = transaction?.smartAccountAddress
  const chainId = transaction?.chainId

  const addressesToWatch = useMemo(() => {
    if (!smartAccountAddress) return []
    return [
      smartAccountAddress,
      ...(transaction?.transactionRecipients?.map((recipient) =>
        Address.format(recipient.recipientAddress),
      ) ?? []),
    ]
  }, [smartAccountAddress, transaction?.transactionRecipients])

  const normalizedCalls = useMemo<BackendTransactionDecodedCall[]>(() => {
    if (!transaction) return []

    // Always derive batch simulation calls from `batchOperations` (present in
    // both draft and submitted states) to keep the query key stable across
    // submit and reuse the cached draft simulation.
    const batchOperations =
      ('batchOperations' in transaction && transaction.batchOperations) || []
    if (batchOperations.length > 0) {
      return batchOperations.flatMap((operation) =>
        operation.calls.map((call) => ({
          to: call.to,
          value: BigInt(call.value),
          data: call.calldata as `0x${string}`,
          function: call.functionSignature?.split('(')[0],
          args: call.args?.map((arg) => ({
            name: arg.name ?? '',
            value: arg.value,
          })),
        })),
      )
    }

    if (!transaction.calls?.length) return []
    return transaction.calls.map((call, index) => ({
      to: call.to,
      value: BigInt(call.value),
      data: call.data,
      function: transaction.decodedCalls?.[index]?.function,
      args: transaction.decodedCalls?.[index]?.args,
    }))
  }, [transaction])

  const simulationUrl = transaction?.simulation?.simulationURL

  if (txnData) {
    return {
      transactionId: undefined,
      smartAccountAddress: txnData.smartAccountAddress,
      chainId: txnData.chainId,
      calls: txnData.calls,
      addressesToWatch: txnData.addressesToWatch,
    }
  }

  if (!transaction) {
    return {
      transactionId: undefined,
      smartAccountAddress: undefined,
      chainId: undefined,
      calls: [],
      addressesToWatch: [],
    }
  }

  return {
    transactionId: transaction.id,
    smartAccountAddress,
    chainId,
    calls: normalizedCalls,
    addressesToWatch,
    simulationUrl,
  }
}
